import { useState } from 'react'
import { appUrl } from '@astro/config'
import { useEnrollments } from '../useDashboard'
import cover1 from '../assets/course-1.webp'
import cover2 from '../assets/course-2.webp'
import cover3 from '../assets/course-3.webp'
import cover4 from '../assets/course-4.webp'

/* ===================== MY ENROLMENTS (seeker side) =====================
   The two "what I'm learning" cards — My Enrolled Courses (self-paced,
   status-filterable) and My Enrolled Live Courses — live here so BOTH
   Academy screens can show them: the seeker/student CoursesTab, and the
   instructor screen for accounts that ALSO hold the student role (an
   instructor who is enrolled somewhere still needs to find their own
   courses). Rows come from GET /enrollments — no demo data. */

const courseHref = (ref) => appUrl('education', ref != null ? `/classes/${ref}` : '/classes')
const payHref = (ref) => appUrl('education', `/classes/${ref}/enroll`)

const rupees = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

/** The outstanding-balance strip on a part-payment enrollment, and the way in
 *  to paying it. Without this the due-date reminder pushes had nowhere to send
 *  anyone: a part-paid student is enrolled, so nothing else on this card hints
 *  that money is still owed. Overdue is called out in red rather than left for
 *  the student to work out from the date. */
function BalanceDueStrip({ courseRef, payment }) {
  const due = payment.dueDate ? new Date(payment.dueDate) : null
  const overdue = Boolean(due && due < new Date())
  const dueText =
    due && !Number.isNaN(due.getTime())
      ? due.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
      : null

  return (
    <div
      className={`mt-3 rounded-xl border px-3 py-2.5 flex items-center justify-between gap-3 ${
        overdue ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
      }`}
    >
      <div className="min-w-0">
        <p className={`text-[11px] font-bold ${overdue ? 'text-red-600' : 'text-amber-700'}`}>
          {rupees(payment.dueNow)} {overdue ? 'overdue' : 'due'}
          {dueText && !overdue ? ` · ${dueText}` : ''}
        </p>
        <p className="text-[10px] text-stone-400 truncate">
          {payment.label ? `${payment.label} · ` : ''}
          {rupees(payment.balance)} left on this course
        </p>
      </div>
      <a
        href={payHref(courseRef)}
        className="shrink-0 bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-wider px-3.5 py-2 rounded-lg hover:shadow-md transition-shadow"
      >
        Pay now
      </a>
    </div>
  )
}

/** Not every live course has a coverImage uploaded — cycle local cover art
 * by list position for the gaps, same as the education app's catalog. */
const FALLBACK_COVERS = [cover1, cover2, cover3, cover4]
const coverFor = (img, i) => img || FALLBACK_COVERS[i % FALLBACK_COVERS.length]

function EnrolledSkeletonCard() {
  return (
    <div className="rounded-2xl border border-stone-100 overflow-hidden animate-pulse" aria-hidden="true">
      <div className="h-32 bg-stone-100" />
      <div className="p-4 space-y-2.5">
        <div className="h-3.5 w-3/4 bg-stone-100 rounded-full" />
        <div className="h-2.5 w-1/2 bg-stone-100 rounded-full" />
        <div className="h-1.5 w-full bg-stone-100 rounded-full mt-3" />
        <div className="flex items-center justify-between mt-3">
          <div className="h-2.5 w-20 bg-stone-100 rounded-full" />
          <div className="h-2.5 w-14 bg-stone-100 rounded-full" />
        </div>
      </div>
    </div>
  )
}

/** One self-paced enrollment card — cover, progress bar and resume/review CTA. */
function EnrolledCourseCard({ c, i }) {
  return (
    <div className="rounded-2xl border border-stone-100 overflow-hidden hover:shadow-lg hover:shadow-amber-100/40 transition-shadow">
      <a href={courseHref(c.courseRef)} className="block h-32 overflow-hidden bg-amber-100">
        <img
          src={coverFor(c.img, i)}
          alt={c.title}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_COVERS[i % FALLBACK_COVERS.length] }}
        />
      </a>
      <div className="p-4">
        <p className="text-sm font-semibold text-astroDark">{c.title}</p>
        <p className="text-[11px] text-stone-400 mt-0.5">
          {c.completed ? 'Completed · Certified' : [c.category, c.enrolledOn && `Enrolled ${c.enrolledOn}`].filter(Boolean).join(' · ') || 'In progress'}
        </p>
        <div className="h-1.5 bg-stone-100 rounded-full mt-3 overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r rounded-full ${c.completed ? 'from-green-500 to-green-500' : 'from-goldAccent to-goldPrimary'}`}
            style={{ width: `${Math.min(100, Math.max(0, c.progress))}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className={`text-[10px] font-bold ${c.completed ? 'text-green-600' : 'text-amber-700'}`}>
            {c.completed ? '✓ Completed' : `${Math.round(c.progress)}% complete`}
          </span>
          <a href={courseHref(c.courseRef)} className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline">
            {c.completed ? 'Review →' : 'Resume →'}
          </a>
        </div>
        {c.payment && <BalanceDueStrip courseRef={c.courseRef} payment={c.payment} />}
      </div>
    </div>
  )
}

/** One live-batch enrollment card — LIVE badge, instructor and the next
 * scheduled class (from the course's embedded upcomingLiveClasses). */
function LiveEnrolledCard({ c, i }) {
  return (
    <div className="rounded-2xl border border-stone-100 overflow-hidden hover:shadow-lg hover:shadow-amber-100/40 transition-shadow">
      <a href={courseHref(c.courseRef)} className="relative block h-32 overflow-hidden bg-amber-100">
        <img
          src={coverFor(c.img, i)}
          alt={c.title}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_COVERS[i % FALLBACK_COVERS.length] }}
        />
        <span className="absolute top-2 left-2 text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/90 text-red-600">🔴 Live</span>
      </a>
      <div className="p-4">
        <p className="text-sm font-semibold text-astroDark">{c.title}</p>
        <p className="text-[11px] text-stone-400 mt-0.5">
          {[c.instructor && `with ${c.instructor}`, c.enrolledOn && `Enrolled ${c.enrolledOn}`].filter(Boolean).join(' · ') || c.category || 'Live batch'}
        </p>
        <div className="rounded-xl bg-red-50/60 border border-red-100 px-3 py-2 mt-3">
          <p className="text-[9px] font-bold uppercase tracking-wider text-red-500">
            {c.nextClassIsNow ? '● Live now' : 'Next live class'}
          </p>
          <p className="text-[11px] font-semibold text-astroDark mt-0.5">
            {[c.nextClassTitle, c.nextClassLabel].filter(Boolean).join(' · ') || 'Schedule to be announced'}
          </p>
        </div>
        <div className="flex items-center justify-end mt-3">
          <a href={courseHref(c.courseRef)} className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline">
            Go to Live Classes →
          </a>
        </div>
        {c.payment && <BalanceDueStrip courseRef={c.courseRef} payment={c.payment} />}
      </div>
    </div>
  )
}

const ENROLL_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
]

/** Both enrolment sections — one hook call, identical wherever it renders. */
export default function EnrolledCoursesSections() {
  const { enrollments, hasEnrollments, isLoading: enrollmentsLoading } = useEnrollments()

  // Live batches get their own section below; the self-paced (recorded)
  // enrollments stay under "My Enrolled Courses" behind the status filter.
  const liveEnrollments = enrollments.filter((c) => c.isLive)
  const recordedEnrollments = enrollments.filter((c) => !c.isLive)

  const [enrollFilter, setEnrollFilter] = useState('all')
  const filterCounts = {
    all: recordedEnrollments.length,
    active: recordedEnrollments.filter((c) => !c.completed).length,
    completed: recordedEnrollments.filter((c) => c.completed).length,
  }
  const visibleEnrollments =
    enrollFilter === 'all'
      ? recordedEnrollments
      : recordedEnrollments.filter((c) => (enrollFilter === 'completed' ? c.completed : !c.completed))

  return (
    <>
      {/* ── The seeker's self-paced enrollments, filterable by status ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <h3 className="text-xl text-astroDark font-royal">My Enrolled Courses</h3>
          {!enrollmentsLoading && recordedEnrollments.length > 0 && (
            <div className="flex flex-wrap gap-1 bg-stone-100/80 rounded-xl p-1 w-max max-w-full">
              {ENROLL_FILTERS.map((f) => {
                const active = f.key === enrollFilter
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setEnrollFilter(f.key)}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${active ? 'bg-[#3D0A0A] text-white shadow-md' : 'text-stone-500 hover:text-astroDark hover:bg-white'}`}
                  >
                    {f.label}
                    <span className={`num text-[9px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[#D4A017] text-[#3D0A0A]' : 'bg-stone-200/80 text-stone-500'}`}>
                      {filterCounts[f.key]}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
        {!enrollmentsLoading && !hasEnrollments ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🎓</div>
            <p className="text-base font-semibold text-astroDark mb-1">No enrollments yet</p>
            <p className="text-sm text-stone-400 mb-4">Enroll in a course from the Vedic Academy and it will appear here.</p>
            <a
              href={courseHref()}
              className="inline-block bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
            >
              Browse Courses
            </a>
          </div>
        ) : !enrollmentsLoading && recordedEnrollments.length === 0 ? (
          <p className="text-sm text-stone-400 py-4">
            All your enrollments are live batches — find them under My Enrolled Live Courses below.
          </p>
        ) : !enrollmentsLoading && visibleEnrollments.length === 0 ? (
          <p className="text-sm text-stone-400 py-4">
            {enrollFilter === 'completed'
              ? 'No completed courses yet — keep learning!'
              : 'Nothing in progress — every course here is completed. 🎉'}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {enrollmentsLoading
              ? Array.from({ length: 2 }, (_, i) => <EnrolledSkeletonCard key={i} />)
              : visibleEnrollments.map((c, i) => <EnrolledCourseCard key={c.key} c={c} i={i} />)}
          </div>
        )}
      </div>

      {/* ── The seeker's live-batch enrollments. Hidden while there are no
             enrollments at all (the empty state above already covers that). ── */}
      {(enrollmentsLoading || hasEnrollments) && (
        <div className="dash-card p-4 sm:p-6">
          <span className="eyebrow text-amber-700">Live Batches</span>
          <h3 className="text-xl text-astroDark mt-1 mb-5 font-royal">My Enrolled Live Courses</h3>
          {!enrollmentsLoading && liveEnrollments.length === 0 ? (
            <div className="py-8 text-center">
              <div className="text-4xl mb-3 opacity-40">🔴</div>
              <p className="text-base font-semibold text-astroDark mb-1">No live course enrollments yet</p>
              <p className="text-sm text-stone-400 mb-4">Join a live batch to learn in real time with the instructor.</p>
              <a
                href={courseHref()}
                className="inline-block bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
              >
                Browse Live Courses
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {enrollmentsLoading
                ? Array.from({ length: 2 }, (_, i) => <EnrolledSkeletonCard key={i} />)
                : liveEnrollments.map((c, i) => <LiveEnrolledCard key={c.key} c={c} i={i} />)}
            </div>
          )}
        </div>
      )}
    </>
  )
}
